import {
    AddOrderWatchersCommandInput,
    AddOrderWatchersCommandOutput,
    AddSubLineItemCommandInput,
    AddSubLineItemCommandOutput,
    DeleteOrderWatchersCommandInput,
    DeleteOrderWatchersCommandOutput,
    GetCommentsCommandInput,
    GetCommentsCommandOutput,
    GetNotesCommandInput,
    GetNotesCommandOutput,
    GetOrder_OrderReferenceCommandInput,
    GetOrderCommandInput,
    GetOrderCommandOutput,
    GetOrders_AllCommandInput,
    GetOrders_AllCommandOutput,
    Order,
    PostCommentCommandInput,
    PostCommentCommandOutput,
    PostLineCommentBulkCommandInput,
    PostLineCommentBulkCommandOutput,
    PostLineCommentCommandInput,
    PostLineCommentCommandOutput,
    PostNoteCommandInput,
    PostNoteCommandOutput,
    UpdateItemDetailsCommandInput,
    UpdateItemDetailsCommandOutput,
    UpdateOneOrderStatus,
    UpdateOrderStatusBatchCommandInput,
    UpdateOrderStatusBatchCommandOutput,
    UpdateOrderStatusCommandInput,
    UpdateOrderStatusCommandOutput,
    UpdateSubLineItemCommandInput,
    UpdateSubLineItemCommandOutput
} from "@amzn/ito-client";
import { CALLER_ABORTED } from "@amzn/sentry-fetch-http-handler";
import {
    MetadataBearer
} from "@aws-sdk/types";
import { isProcurement } from "common/utils";
import { useEffect, useState } from "react";

import { getITOClient } from "./hooks-helpers";
import { AMZNHAM_CONSUMER, HAM_CONSUMER, ITSE_ROLE, PROCUREMENT } from "interfaces/constants";
import { UserPermissions } from "services/user-permissions";
import { PERMISSIONS } from "configuration/config";
import { statusRulesService } from "services/status-rules-service";

const NO_RESPONSE_ERROR = { name: "No response", message: "No response" };

async function throwOnHttpError(response: Response): Promise<void> {
    if (!response.ok) {
        const errorBody = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorBody.message || response.statusText);
    }
}

export interface GetOrdersProps {
    clearPreviousData?: boolean;
    pageSize?: number;
    pageIndex?: number;
    filters?: Record<string, string[]>;
    defaultFilters?: Record<string, string[]>;
    isAmznHam?: boolean,
}

export interface GetFilesProps {
    clearPreviousData?: boolean;
    pageSize?: number;
    pageIndex?: number;
    fromAutoRefresh?: boolean;
}

export interface ApiError extends Error, MetadataBearer {

}

// Client
const { client, requestHandler } = getITOClient();

interface GetCommentsProps {
    taxonomyId: string;
    clearPreviousData?: boolean;
    pageSize?: number;
    pageIndex?: number;
    fromAutoRefresh?: boolean;
    filters?: Record<string, string[]>;
}

type GetOrderResult = GetOrders_AllCommandOutput | null | undefined;
export function useGetOrders(
    mainProps: GetOrdersProps,
    defaultValue: GetOrderResult,
): { ordersData: GetOrderResult; isLoading: boolean; doGetOrders: (props: GetOrdersProps) => void } {
    const [ordersData, setData] = useState<GetOrderResult>(defaultValue);
    const [isLoading, setLoading] = useState<boolean>(false);

    function doGetOrders(props: GetOrdersProps) {
        // its loading now
        setLoading(true);

        const selectedClient = props.isAmznHam ? getITOClient(AMZNHAM_CONSUMER).client : client;

        // Pagination
        if (!props.pageIndex) {
            props.pageIndex = 1;
        }
        if (!props.pageSize) {
            props.pageSize = 30;
        }

        // Filtering
        if (!props.filters) {
            props.filters = {};
        }

        // Clear prev data
        if (props.clearPreviousData && props.clearPreviousData === true) {
            setData(undefined);
        }
        const request: GetOrders_AllCommandInput = {
            pageSize: props.pageSize,
            nextToken: (props.pageIndex - 1).toString(),
            filters: props.filters,
        };

        selectedClient.getOrders_All(
            request,
            {},
            (err: Error, response?: GetOrders_AllCommandOutput | undefined) => {
                /* istanbul ignore next */
                if (err && err.message === CALLER_ABORTED) {
                    return;
                }

                /* istanbul ignore next */
                if (!response || !response.orders) {
                    console.error(err);
                    throw new Error("No orders data found");
                }

                setData(response);
                setLoading(false);
            },
        );
    }

    if (defaultValue === null) {
        // No default value, then get from API
        useEffect(() => {
            doGetOrders(mainProps);
        }, [defaultValue]);
    }

    return { ordersData, isLoading, doGetOrders };
}

export function useGetOrder(
    id: string | undefined,
    defaultValue?: GetOrderCommandOutput,
): { data: Order | undefined; isLoading: boolean; error: Error | undefined; doGetOrder: (id: string | undefined) => void } {
    const [data, setData] = useState<GetOrderCommandOutput | undefined>(defaultValue);
    const [error, setError] = useState<Error | undefined>(undefined);
    const [isLoading, setLoading] = useState<boolean>(true);

    function doGetOrder(id: string | undefined) {
        if (id && /^\d+$/.test(id)) {
            const request: GetOrder_OrderReferenceCommandInput = { orderReference: id };
            client.getOrder_OrderReference(request, (err: any, response?: GetOrderCommandOutput | undefined) => {
                if (!response || err) {
                    setError({ name: "Unable to get the order details", message: `Error: ${err}` });
                }
                else {
                    setError(undefined);
                    setData(response);
                }
                setLoading(false);
            });
        } else {
            const request: GetOrderCommandInput = { orderId: id };
            client.getOrder(request, (err: any, response?: GetOrderCommandOutput | undefined) => {
                if (!response || err) {
                    setError({ name: "Unable to get the order details", message: `Error: ${err}` });
                }
                else {
                    setError(undefined);
                    setData(response);
                }
                setLoading(false);
            });
        }
    }

    // Order
    if (!defaultValue) {
        useEffect(() => {
            doGetOrder(id);
        }, []);
    }

    return { data, error, isLoading, doGetOrder };
}

export type GetCommentsResult = GetCommentsCommandOutput | null | undefined;
export function useGetComments(defaultValue: GetCommentsResult) {
    const [comments, setComments] = useState<GetCommentsResult>(defaultValue);
    const [isLoading, setLoading] = useState<boolean>(false);
    const [errorForGetComments, setError] = useState<ApiError | null>(null);

    function doGetComments(props: GetCommentsProps) {
        if (!props.fromAutoRefresh) {
            setLoading(true);
        }

        // Filtering
        if (!props.filters) props.filters = {};

        const request: GetCommentsCommandInput = {
            taxonomyId: props.taxonomyId,
            filters: props.filters,
            pageSize: 100,
        };

        client.getComments(request, (err: ApiError, response?: GetCommentsCommandOutput | undefined) => {
            if (err && err.message === CALLER_ABORTED) {
                return;
            }
            if (err) {
                setError(err);
                setComments({
                    ...err,
                    totalResults: 0,
                    pageSize: 0,
                    comments: []
                });
                setLoading(false);
                return;
            }

            if (!response || !response.comments) {
                console.error(err);
                throw new Error("No files data found");
            }
            // sort the comments by their posted date (newest first)
            response.comments.sort((commentA, commentB) => {
                const commentAPublishedDate = new Date(commentA.posted!.on!).getTime();
                const commentBPublishedDate = new Date(commentB.posted!.on!).getTime();

                return commentBPublishedDate - commentAPublishedDate;
            });
            setComments(response);
            setLoading(false);
        });
    }
    return { comments, isLoading, doGetComments, errorForGetComments, setComments };
}

export function usePostComment(amznham?: boolean) {
    const [error, setError] = useState<Error | null>(null);
    const [response, setResponse] = useState<PostCommentCommandOutput | undefined>(undefined);
    const [isLoading, setLoading] = useState<boolean>(false);

    const { client, requestHandler } = getITOClient(amznham ? AMZNHAM_CONSUMER : undefined);

    async function doPostComment(taxonomyId: string, comment: string, csrfToken: string, tags: string[], isResponseRequired?: boolean) {
        setLoading(true);

        requestHandler.pushHeader("x-csrf-token", csrfToken);

        if (await isProcurement()) {
            tags.push(PROCUREMENT);
        } else if (UserPermissions.instance.hasPermission(PERMISSIONS.ITSE_SUPPORT)
            && UserPermissions.instance.hasPermission(PERMISSIONS.ENABLE_ITSE_SUPPORT)) {
            tags.push(ITSE_ROLE);
        }

        const request: PostCommentCommandInput = {
            taxonomyId,
            comment,
            tags,
            ...(isResponseRequired !== undefined && { isResponseRequired }),
        };

        client.postComment(request, (err: Error, response?: PostCommentCommandOutput | undefined) => {
            if (err && err.message === CALLER_ABORTED) {
                return;
            }

            if (!response) {
                console.error(err);
                setError(error);
                throw new Error("No comment was posted");
            }
            setResponse(response);
            setLoading(false);
        });
    }

    return { response, isLoading, error, doPostComment };
}

export function usePostLineComment() {
    const [error, setError] = useState<Error | null>(null);
    const [response, setResponse] = useState<PostLineCommentCommandOutput | PostLineCommentBulkCommandOutput | undefined>(undefined);
    const [isLoading, setLoading] = useState<boolean>(false);

    async function doPostLineComment(taxonomyIds: string[], comment: string, csrfToken: string, tags: string[]) {
        setLoading(true);

        requestHandler.pushHeader("x-csrf-token", csrfToken);

        if (await isProcurement()) {
            tags.push(PROCUREMENT);
        }

        try {
            if (taxonomyIds.length == 1) {
                postSingleLineComment(taxonomyIds[0], comment, tags);
            } else if (taxonomyIds.length > 1) {
                postBulkLineComment(taxonomyIds, comment, tags)
            } else {
                setError(new Error("No line items found."));
                setResponse(undefined);
            }
        } catch (err) {
            setError(err as Error);
        }
        setLoading(false);
    }

    async function postSingleLineComment(taxonomy: string, comment: string, tags: string[]) {
        const lineRequest: PostLineCommentCommandInput = {
            taxonomyId: taxonomy,
            comment,
            tags,
        };
        client.postLineComment(lineRequest, (err: Error, response?: PostLineCommentCommandOutput | undefined) => {
            if (!response) {
                const fault = new Error(`Did not receive response from ITO. ${err.message}`)
                console.error(fault);
                setError(fault);
            }
            setResponse(response);
        });
    }

    async function postBulkLineComment(taxonomyIds: string[], comment: string, tags: string[]) {
        const bulkRequest: PostLineCommentBulkCommandInput = {
            taxonomyIds,
            comment,
            tags,
        };
        client.postLineCommentBulk(bulkRequest, (err: Error, response?: PostLineCommentBulkCommandOutput | undefined) => {
            if (!response) {
                const fault = new Error(`Did not receive response from ITO. ${err.message}`)
                console.error(fault);
                setError(fault);
            }
            setResponse(response);
        });
    }

    return { response, isLoading, error, doPostLineComment };
}

export type GetNotesResult = GetCommentsCommandOutput | null | undefined;
export function useGetNotes(defaultValue: GetNotesResult) {
    const [notes, setNotes] = useState<GetNotesResult>(defaultValue);
    const [isLoading, setLoading] = useState<boolean>(false);
    const [errorForGetNotes, setError] = useState<ApiError | null>(null);

    function doGetNotes(props: GetCommentsProps) {
        if (!props.fromAutoRefresh) {
            setLoading(true);
        }

        // Filtering
        if (!props.filters) props.filters = {};

        const request: GetNotesCommandInput = {
            taxonomyId: props.taxonomyId,
            filters: props.filters,
            pageSize: 100,
        };

        client.getNotes(request, (err: ApiError, response?: GetNotesCommandOutput | undefined) => {
            if (err && err.message === CALLER_ABORTED) {
                return;
            }
            if (err) {
                setError(err);
                setNotes({
                    ...err,
                    totalResults: 0,
                    pageSize: 0,
                    comments: []
                });
                setLoading(false);
                return;
            }
            if (!response || !response.comments) {
                console.error(err);
                setLoading(false);
                throw new Error("No files data found");
            }
            // sort the comments by their posted date (newest first)
            response.comments.sort((commentA, commentB) => {
                const commentAPublishedDate = new Date(commentA.posted!.on!).getTime();
                const commentBPublishedDate = new Date(commentB.posted!.on!).getTime();

                return commentBPublishedDate - commentAPublishedDate;
            });
            setNotes(response);
            setLoading(false);
        });
    }
    return { notes, isLoading, doGetNotes, setNotes, errorForGetNotes };
}

export function usePostNote() {
    const [error, setError] = useState<Error | null>(null);
    const [response, setResponse] = useState<PostNoteCommandOutput | undefined>(undefined);
    const [isLoading, setLoading] = useState<boolean>(false);

    async function doPostNote(taxonomyId: string, comment: string, csrfToken: string, tags: string[]) {
        setLoading(true);

        requestHandler.pushHeader("x-csrf-token", csrfToken);

        if (await isProcurement()) {
            tags.push(PROCUREMENT);
        }

        const request: PostNoteCommandInput = {
            taxonomyId,
            comment,
            tags,
        };

        client.postNote(request, (err: Error, response?: PostNoteCommandOutput | undefined) => {
            if (err && err.message === CALLER_ABORTED) {
                return;
            }

            if (!response) {
                console.error(err);
                setError(err);
                setLoading(false);
                throw new Error("No note was posted");
            }
            setResponse(response);
            setLoading(false);
        });
    }

    return { response, isLoading, error, doPostNote };
}

// Omit the order id since it will receive an array of orders
interface UpdateOneOrderProps extends Omit<UpdateOneOrderStatus, "orderId"> { }

export function useUpdateOrdersStatus() {
    const [error, setError] = useState<Error | null>(null);
    const [isLoading, setLoading] = useState(false);
    const [data, setData] = useState<UpdateOrderStatusBatchCommandOutput | UpdateOrderStatusCommandOutput | undefined>(
        undefined,
    );

    /**
     * Receives a list of orders ids and one UpdateOneOrder request that will be applied to all orders.
     */
    function doUpdateOrdersById(orderIds: string[], updateProps: UpdateOneOrderProps, csrfToken: string) {
        if (orderIds.length === 0) {
            setError(new Error("Orders list is empty."));
            return;
        }

        const updateRequests: UpdateOneOrderStatus[] = orderIds.map((orderId) => {
            return { ...updateProps, orderId: orderId };
        });

        doUpdateOrderStatus(updateRequests, csrfToken);
    }

    /**
     * Receives a list of orders and one UpdateOrder request that will be applied to all orders.
     * But first performs the first assignment logic to update the order status.
     */
    function doUpdateOrders(orders: Order[], updateProps: UpdateOneOrderProps, csrfToken: string, updateOnAmznHam: boolean = false) {
        if (orders.length === 0) {
            setError(new Error("Orders list is empty."));
            return;
        }

        const updateRequests: (UpdateOneOrderStatus | undefined)[] = orders.map((order) => {
            const details = order.details as any;

            // If the order is blocked it can not be updated
            if (details && details.firstEdit && details.firstEdit !== "ham") {
                return undefined;
            }

            let request = { ...updateProps, orderId: order.orderId };


            return request;
        });
        // Get only the requests that are not undefined
        const definedRequests = updateRequests.filter(request => request !== undefined) as UpdateOneOrderStatus[];

        doUpdateOrderStatus(definedRequests, csrfToken, updateOnAmznHam);
    }

    function getClientAndHandler(updateOnAmznHam: boolean) {
        if (updateOnAmznHam === true) {
            const { client: amznHamClient, requestHandler: amznHamRequestHandler } = getITOClient(AMZNHAM_CONSUMER);
            return { currentClient: amznHamClient, currentRequestHandler: amznHamRequestHandler };
        } else {
            return { currentClient: client, currentRequestHandler: requestHandler };
        }
    }

    function doUpdateOrderStatus(updateRequests: UpdateOneOrderStatus[], csrfToken: string, updateOnAmznHam: boolean = false) {
        if (updateRequests.length > 1) {
            doUpdateOrderStatus_Batch(updateRequests, csrfToken, updateOnAmznHam);
        } else {
            doUpdateOrderStatus_One(updateRequests[0], csrfToken, updateOnAmznHam);
        }
    }

    function doUpdateOrderStatus_Batch(orders: UpdateOneOrderStatus[], csrfToken: string, updateOnAmznHam: boolean = false) {
        setLoading(true);
        const { currentClient, currentRequestHandler } = getClientAndHandler(updateOnAmznHam);

        currentRequestHandler.pushHeader("x-csrf-token", csrfToken);

        const request: UpdateOrderStatusBatchCommandInput = {
            orders,
        };

        currentClient.updateOrderStatusBatch(
            request,
            {},
            (err: Error, response?: UpdateOrderStatusBatchCommandOutput | undefined) => {
                /* istanbul ignore next */
                if (err && err.message === CALLER_ABORTED) {
                    return;
                }

                /* istanbul ignore next */
                if (!response || !response.orders) {
                    setLoading(false);
                    console.error(err);
                    setError(err);
                    throw err;
                }
                setData(response);
                setError(null);
                setLoading(false);
            },
        );
    }

    async function doUpdateOrderStatus_One(orderStatusUpdate: UpdateOneOrderStatus, csrfToken: string, updateOnAmznHam: boolean = false) {
        setLoading(true);
        const { currentClient, currentRequestHandler } = getClientAndHandler(updateOnAmznHam);

        const request: UpdateOrderStatusCommandInput = { ...orderStatusUpdate };
        const { orderId, ...payload } = orderStatusUpdate;
        const consumer = updateOnAmznHam ? AMZNHAM_CONSUMER : HAM_CONSUMER;
        const useStatusRulesModule = UserPermissions.instance.hasPermission(PERMISSIONS.USE_STATUS_RULES_ENDPOINTS);

        currentRequestHandler.pushHeader("x-csrf-token", csrfToken);

        if (useStatusRulesModule) {
            try {
                const response = await statusRulesService.updateOrder(orderId!, payload, csrfToken, consumer);
                await throwOnHttpError(response);
                setData({} as UpdateOrderStatusCommandOutput);
                setError(null);
                setLoading(false);
            } catch (err) {
                setError(err as Error);
                setLoading(false);
                console.error(err);
            }
        } else {
            statusRulesService.updateOrder(orderId!, payload, csrfToken, consumer).catch(() => {});

            currentClient.updateOrderStatus(
                request,
                {},
                (err: Error, response?: UpdateOrderStatusCommandOutput | undefined) => {
                    if (err && err.message === CALLER_ABORTED) {
                        return;
                    }

                    if (!response) {
                        setLoading(false);
                        setError(err);
                        console.error(err);
                        throw err;
                    }

                    setData(response);
                    setError(null);
                    setLoading(false);
                },
            );
        }
    }

    return { isLoading, setLoading, data, setData, error, setError, doUpdateOrders, doUpdateOrdersById };
}

export function useUpdateItemDetails() {
    const [error, setError] = useState<Error | null>(null);
    const [isLoading, setLoading] = useState(false);
    const [data, setData] = useState<UpdateItemDetailsCommandOutput | undefined>(undefined);

    async function doUpdateItemDetailsAsync(props: UpdateItemDetailsCommandInput, csrfToken: string) {
        setLoading(true);

        requestHandler.pushHeader("x-csrf-token", csrfToken);

        const useStatusRulesModule = UserPermissions.instance.hasPermission(PERMISSIONS.USE_STATUS_RULES_ENDPOINTS);

        try {
            if (useStatusRulesModule) {
                const { orderId, itemId, ...payload } = props;
                const response = await statusRulesService.updateItem(orderId!, itemId!, payload, csrfToken, HAM_CONSUMER);
                await throwOnHttpError(response);
                setError(null);
                setData({} as UpdateItemDetailsCommandOutput);
            } else {
                const { orderId, itemId, ...payload } = props;
                statusRulesService.updateItem(orderId!, itemId!, payload, csrfToken, HAM_CONSUMER).catch(() => {});
                await new Promise(resolve => setTimeout(resolve, 1000));
                const response = await client.updateItemDetails({ ...props });
                if (!response) {
                    console.error(NO_RESPONSE_ERROR);
                    setError(NO_RESPONSE_ERROR);
                } else {
                    setError(null);
                    setData(response);
                }
            }
        }
        catch (err) {
            console.error(err);
            setError(err as Error);
        }
        setLoading(false);
    }

    return { isLoading, setLoading, data, setData, error, setError, doUpdateItemDetailsAsync };
}

export function useUpdateSubLineItemDetails() {
    const [error, setError] = useState<Error | null>(null);
    const [isLoading, setLoading] = useState(false);
    const [data, setData] = useState<UpdateSubLineItemCommandOutput | undefined>(undefined);

    async function doUpdateSubLineItemAsync(props: UpdateSubLineItemCommandInput, csrfToken: string) {
        setLoading(true);

        requestHandler.pushHeader("x-csrf-token", csrfToken);

        const useStatusRulesModule = UserPermissions.instance.hasPermission(PERMISSIONS.USE_STATUS_RULES_ENDPOINTS);

        try {
            if (useStatusRulesModule) {
                const { orderId, itemId, id: subLineId, ...payload } = props;
                const response = await statusRulesService.updateSubLine(orderId!, itemId!, subLineId!, payload, csrfToken, HAM_CONSUMER);
                await throwOnHttpError(response);
                setError(null);
                setData({} as UpdateSubLineItemCommandOutput);
            } else {
                const { orderId, itemId, id: subLineId, ...payload } = props;
                statusRulesService.updateSubLine(orderId!, itemId!, subLineId!, payload, csrfToken, HAM_CONSUMER).catch(() => {});
                await new Promise(resolve => setTimeout(resolve, 1000));
                const response = await client.updateSubLineItem({ ...props });
                if (!response) {
                    console.error(NO_RESPONSE_ERROR);
                    setError(NO_RESPONSE_ERROR);
                } else {
                    setError(null);
                    setData(response);
                }
            }
        }
        catch (err) {
            console.error(err);
            setError(err as Error);
        }

        setLoading(false);
    }

    return { isLoading, setLoading, data, setData, error, setError, doUpdateSubLineItemAsync };
}

export function useAddOrderSublineItem() {
    const [error, setError] = useState<Error | null>(null);
    const [response, setResponse] = useState<AddSubLineItemCommandOutput | undefined>(undefined);
    const [isLoading, setLoading] = useState<boolean>(false);

    async function doAddSublineItemAsync(props: AddSubLineItemCommandInput, csrfToken: string) {
        requestHandler.pushHeader("x-csrf-token", csrfToken);

        const useStatusRulesModule = UserPermissions.instance.hasPermission(PERMISSIONS.USE_STATUS_RULES_ENDPOINTS);

        try {
            if (useStatusRulesModule) {
                const { orderId, itemId, ...payload } = props;
                const response = await statusRulesService.createSubLine(orderId!, itemId!, payload, csrfToken, HAM_CONSUMER);
                await throwOnHttpError(response);
                setError(null);
                setResponse({} as AddSubLineItemCommandOutput);
            } else {
                const { orderId, itemId, ...payload } = props;
                statusRulesService.createSubLine(orderId!, itemId!, payload, csrfToken, HAM_CONSUMER).catch(() => {});
                await new Promise(resolve => setTimeout(resolve, 1000));
                const response = await client.addSubLineItem({ ...props });
                if (!response) {
                    console.error(NO_RESPONSE_ERROR);
                    setError(NO_RESPONSE_ERROR);
                } else {
                    setError(null);
                    setResponse(response);
                }
            }
        }
        catch (err) {
            console.error(err);
            setError(err as Error);
        }

        setLoading(false);
    }

    return { isLoading, response, error, doAddSublineItemAsync };
}

export function useAddOrderWatchers() {
    const [response, setResponse] = useState<Order | undefined>(undefined);
    const [isLoading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | undefined>(undefined);

    async function doAddOrderWatchers(orderId: string, watcher: string, csrfToken: string) {
        setLoading(true);
        requestHandler.pushHeader("x-csrf-token", csrfToken);
        const request: AddOrderWatchersCommandInput = {
            orderId,
            watcher
        };
        try {
            const response = await client.addOrderWatchers(request);
            setError(undefined);
            setResponse(response);
        } catch (err) {
            setError(err as Error);
        }
        setLoading(false);
    }

    return { response, isLoading, error, doAddOrderWatchers };
}

export function useRemoveOrderWatchers() {
    const [response, setResponse] = useState<Order | undefined>(undefined);
    const [isLoading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | undefined>(undefined);

    async function doDeleteOrderWatchers(orderId: string, watcher: string, csrfToken: string) {
        setLoading(true);
        requestHandler.pushHeader("x-csrf-token", csrfToken);
        const request: DeleteOrderWatchersCommandInput = {
            orderId,
            watcher
        };
        try {
            const response = await client.deleteOrderWatchers(request);
            setError(undefined);
            setResponse(response);
        } catch (err) {
            setError(err as Error);
        }
        setLoading(false);
    }

    return { response, isLoading, error, doDeleteOrderWatchers };
}
